import { useState } from "react";
import { ArrowDown, ArrowUp, Briefcase } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { PortfolioCard, PositionsTable } from "@/features/trading";
import { asContractPrice, asCurrency, asFiniteNumber, formatRelativeTime } from "@/lib/format";
import {
  useLiveLedger,
  usePortfolioHistory,
  usePortfolioPositions,
  usePortfolioSummary,
} from "@/sdk/hooks/use-portfolio";
import type {
  LiveLedgerFill,
  LiveLedgerOrder,
  PortfolioHistoryEntry,
  PortfolioPosition,
} from "@/sdk/hooks/use-portfolio";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SortField = "pnl" | "size" | "entry_price" | "current_price" | "pnl_pct";
type SortDir = "asc" | "desc";

function pnlTone(value: number): "success" | "warning" | "danger" | "default" {
  if (value > 0) return "success";
  if (value < 0) return "danger";
  return "default";
}

function pnlColor(value: number): string {
  if (value > 0) return "text-green-400";
  if (value < 0) return "text-red-400";
  return "text-white/50";
}

function pnlBg(value: number): string {
  if (value > 0) return "bg-green-500/10 border-green-500/20";
  if (value < 0) return "bg-red-500/10 border-red-500/20";
  return "border-white/5";
}

function dirBadge(side: string): string {
  if (side === "BUY") return "bg-emerald-500/20 text-emerald-300";
  if (side === "SELL") return "bg-red-500/20 text-red-300";
  return "bg-white/10 text-white/55";
}

function formatPnl(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${asCurrency(value)}`;
}

function formatPct(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

function formatSize(value: unknown): string {
  const parsed = asFiniteNumber(value);
  return parsed === null ? "-" : parsed.toFixed(2);
}

/**
 * Degraded-backend banner: the endpoint answered 200 with an empty payload and
 * an explanatory `note` (e.g. "Live ledger requires PostgreSQL."). That empty
 * payload is NOT evidence of "no data" — surface the note, never a calm empty
 * state, on money-relevant panels.
 */
function DegradedNote({ title, note }: { title: string; note: string }) {
  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
      <p className="text-sm text-amber-300">{title}</p>
      <p className="mt-0.5 text-xs text-white/40">{note}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortHeader
// ---------------------------------------------------------------------------

function SortHeader({
  label,
  field,
  active,
  dir,
  onSort,
}: {
  label: string;
  field: SortField;
  active: boolean;
  dir: SortDir;
  onSort: (f: SortField) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="flex items-center gap-1 text-xs font-medium text-white/60 hover:text-white/90 transition"
    >
      {label}
      {active &&
        (dir === "desc" ? (
          <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUp className="h-3 w-3" />
        ))}
    </button>
  );
}

// ---------------------------------------------------------------------------
// PositionRow
// ---------------------------------------------------------------------------

function PositionRow({ position }: { position: PortfolioPosition }) {
  const pnl =
    position.status === "open"
      ? position.unrealized_pnl
      : (position.realized_pnl ?? 0);

  return (
    <div
      className={`grid grid-cols-[1fr_4rem_5rem_5rem_5rem_5rem_5rem] items-center gap-3 rounded border px-3 py-2 ${pnlBg(pnl)}`}
    >
      {/* Market name */}
      <div className="min-w-0">
        <p className="truncate text-xs text-white/80">
          {position.question || position.market_id}
        </p>
        <p className="text-[0.65rem] text-white/35">
          {position.strategy ? `${position.strategy} ` : ""}
          {position.entry_time ? formatRelativeTime(position.entry_time) : ""}
        </p>
      </div>

      {/* Direction */}
      <div className="text-center">
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${dirBadge(position.side)}`}
        >
          {position.side === "BUY" ? "YES" : "NO"}
        </span>
      </div>

      {/* Size */}
      <div className="text-right">
        <span className="text-xs font-mono text-white/70">
          {asCurrency(position.size)}
        </span>
      </div>

      {/* Entry price */}
      <div className="text-right">
        <span className="text-xs font-mono text-white/70">
          {position.entry_price.toFixed(2)}
        </span>
      </div>

      {/* Current price */}
      <div className="text-right">
        <span className="text-xs font-mono text-white/70">
          {position.current_price.toFixed(2)}
        </span>
      </div>

      {/* P&L */}
      <div className="text-right">
        <span className={`text-xs font-mono font-semibold ${pnlColor(pnl)}`}>
          {formatPnl(pnl)}
        </span>
      </div>

      {/* P&L % */}
      <div className="text-right">
        <span className={`text-xs font-mono ${pnlColor(position.pnl_pct)}`}>
          {formatPct(position.pnl_pct)}
        </span>
      </div>
    </div>
  );
}

// Ledger states from backend/shared/order_ledger_pg.py. Failure states must
// out-rank the filled_size>0 green: a partially-filled order that ends
// FAILED/RECONCILE_REQUIRED is the row an operator must notice, not a win.
const ORDER_FAILURE_STATES = new Set(["REJECTED", "FAILED", "RECONCILE_REQUIRED"]);
const ORDER_WARNING_STATES = new Set(["CANCELLED", "EXPIRED"]);

function orderStateTone(order: LiveLedgerOrder): string {
  if (ORDER_FAILURE_STATES.has(order.state)) {
    return "border-red-500/40 bg-red-500/10 text-red-300";
  }
  if (ORDER_WARNING_STATES.has(order.state)) {
    return "border-amber-500/40 bg-amber-500/10 text-amber-300";
  }
  if (order.state === "PARTIALLY_FILLED") {
    return "border-cyan-500/30 bg-cyan-500/10 text-cyan-300";
  }
  if (order.filled_size > 0) {
    return "border-emerald-500/20 bg-emerald-500/10 text-emerald-300";
  }
  if (order.state === "ACKED" || order.state === "OPEN") {
    return "border-blue-500/20 bg-blue-500/10 text-blue-300";
  }
  return "border-white/10 bg-white/5 text-white/60";
}

function LiveOrderRow({ order }: { order: LiveLedgerOrder }) {
  const stateTone = orderStateTone(order);
  const side = typeof order.side === "string" && order.side.trim()
    ? order.side.trim().toUpperCase()
    : "-";

  return (
    <div className="grid grid-cols-[1fr_4rem_5rem_5rem_6rem_6rem] items-center gap-3 rounded border border-white/5 bg-white/[0.02] px-3 py-2">
      <div className="min-w-0">
        <div className="truncate font-mono text-xs text-white/80">
          {order.venue_order_id_redacted || order.order_key.slice(0, 12)}
        </div>
        <div className="truncate text-[11px] text-white/35">
          {order.submitted_at ? formatRelativeTime(order.submitted_at) : order.market_id}
        </div>
      </div>
      <span className={`rounded px-1.5 py-0.5 text-center text-[11px] font-semibold ${dirBadge(side)}`}>
        {side}
      </span>
      <div className="text-right font-mono text-xs text-white/70">
        {asCurrency(order.requested_notional)}
      </div>
      <div className="text-right font-mono text-xs text-white/70">
        {formatSize(order.requested_size)}
      </div>
      <div className="text-right font-mono text-xs text-white/70">
        {formatSize(order.filled_size)}
      </div>
      <div className="text-right">
        <span className={`rounded border px-1.5 py-0.5 text-[11px] font-semibold ${stateTone}`}>
          {order.state}
        </span>
      </div>
    </div>
  );
}

function LiveFillRow({ fill }: { fill: LiveLedgerFill }) {
  const side = typeof fill.side === "string" && fill.side.trim()
    ? fill.side.trim().toUpperCase()
    : "-";
  const fillSize = asFiniteNumber(fill.fill_size);
  const feePaid = asFiniteNumber(fill.fee_paid);

  return (
    <div className="grid grid-cols-[1fr_4rem_5rem_5rem_5rem_6rem] items-center gap-3 rounded border border-white/5 bg-white/[0.02] px-3 py-2">
      <div className="min-w-0">
        <div className="truncate font-mono text-xs text-white/80">
          {fill.venue_order_id_redacted || String(fill.fill_id)}
        </div>
        <div className="truncate text-[11px] text-white/35">
          {fill.venue_filled_at
            ? formatRelativeTime(fill.venue_filled_at)
            : fill.ingested_at
              ? formatRelativeTime(fill.ingested_at)
              : fill.market_id}
        </div>
      </div>
      <span className={`rounded px-1.5 py-0.5 text-center text-[11px] font-semibold ${dirBadge(side)}`}>
        {side}
      </span>
      <div className="text-right font-mono text-xs text-white/70">
        {fillSize === null ? "-" : fillSize.toFixed(2)}
      </div>
      <div className="text-right font-mono text-xs text-white/70">
        {asContractPrice(fill.fill_price)}
      </div>
      <div className="text-right font-mono text-xs text-white/70">
        {feePaid === null ? "n/a" : asCurrency(feePaid)}
      </div>
      <div className="text-right">
        <span className="rounded border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-300">
          {fill.source || "fill"}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PnlChart — simple SVG line chart
// ---------------------------------------------------------------------------

function PnlChart({ data }: { data: PortfolioHistoryEntry[] }) {
  if (data.length < 2) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-white/30">
        Not enough data for chart (need 2+ days).
      </div>
    );
  }

  const width = 800;
  const height = 180;
  const padX = 40;
  const padY = 20;

  const values = data.map((d) => d.cumulative_pnl);
  const minVal = Math.min(...values, 0);
  const maxVal = Math.max(...values, 0);
  const range = maxVal - minVal || 1;

  const plotW = width - 2 * padX;
  const plotH = height - 2 * padY;

  const points = data.map((d, i) => {
    const x = padX + (i / (data.length - 1)) * plotW;
    const y = padY + plotH - ((d.cumulative_pnl - minVal) / range) * plotH;
    return { x, y, ...d };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  // Fill area under line down to zero-line
  const zeroY = padY + plotH - ((0 - minVal) / range) * plotH;
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${zeroY} L ${points[0].x} ${zeroY} Z`;

  const lastVal = values[values.length - 1];
  const lineColor = lastVal >= 0 ? "#22c55e" : "#ef4444";
  const fillColor = lastVal >= 0 ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)";

  // Y-axis gridlines
  const gridCount = 4;
  const gridLines = Array.from({ length: gridCount + 1 }, (_, i) => {
    const val = minVal + (range / gridCount) * i;
    const y = padY + plotH - ((val - minVal) / range) * plotH;
    return { val, y };
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto min-h-[120px] md:min-h-[160px]" preserveAspectRatio="xMidYMid meet">
      {/* Grid lines */}
      {gridLines.map((g) => (
        <g key={g.val}>
          <line
            x1={padX}
            y1={g.y}
            x2={width - padX}
            y2={g.y}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
          />
          <text
            x={padX - 4}
            y={g.y + 3}
            textAnchor="end"
            fill="rgba(255,255,255,0.3)"
            fontSize={9}
            fontFamily="monospace"
          >
            {g.val >= 0 ? "+" : ""}{g.val.toFixed(0)}
          </text>
        </g>
      ))}

      {/* Zero line */}
      <line
        x1={padX}
        y1={zeroY}
        x2={width - padX}
        y2={zeroY}
        stroke="rgba(255,255,255,0.15)"
        strokeWidth={1}
        strokeDasharray="4 2"
      />

      {/* Area fill */}
      <path d={areaPath} fill={fillColor} />

      {/* Line */}
      <path d={linePath} fill="none" stroke={lineColor} strokeWidth={2} strokeLinejoin="round" />

      {/* Data point dots */}
      {points.length <= 60 &&
        points.map((p) => (
          <circle key={p.date} cx={p.x} cy={p.y} r={2.5} fill={lineColor} />
        ))}

      {/* X-axis labels (first, mid, last) */}
      {[0, Math.floor(data.length / 2), data.length - 1].map((idx) => {
        const p = points[idx];
        if (!p) return null;
        return (
          <text
            key={idx}
            x={p.x}
            y={height - 2}
            textAnchor="middle"
            fill="rgba(255,255,255,0.3)"
            fontSize={9}
            fontFamily="monospace"
          >
            {p.date}
          </text>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// PortfolioPage
// ---------------------------------------------------------------------------

export function PortfolioPage() {
  const summaryQuery = usePortfolioSummary();
  const positionsQuery = usePortfolioPositions({ status: "open", sort: "pnl" });
  const liveLedgerQuery = useLiveLedger(20);
  const historyQuery = usePortfolioHistory(90);

  const [sortField, setSortField] = useState<SortField>("pnl");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const summary = summaryQuery.data;
  const positions = positionsQuery.data?.positions ?? [];
  const liveOrders = liveLedgerQuery.data?.orders ?? [];
  const liveFills = liveLedgerQuery.data?.fills ?? [];
  const history = historyQuery.data?.history ?? [];
  const activeBaseline = [
    summary?.baseline,
    liveLedgerQuery.data?.baseline,
    historyQuery.data?.baseline,
    positionsQuery.data?.baseline,
  ].find((baseline) => baseline?.active);
  const baselineScope = activeBaseline ? "Since go-live baseline" : undefined;

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sorted = [...positions].sort((a, b) => {
    let av: number;
    let bv: number;
    switch (sortField) {
      case "pnl":
        av = a.unrealized_pnl + (a.realized_pnl ?? 0);
        bv = b.unrealized_pnl + (b.realized_pnl ?? 0);
        break;
      case "pnl_pct":
        av = a.pnl_pct;
        bv = b.pnl_pct;
        break;
      case "size":
        av = a.size;
        bv = b.size;
        break;
      case "entry_price":
        av = a.entry_price;
        bv = b.entry_price;
        break;
      case "current_price":
        av = a.current_price;
        bv = b.current_price;
        break;
      default:
        av = 0;
        bv = 0;
    }
    return sortDir === "desc" ? bv - av : av - bv;
  });

  const isLive =
    summaryQuery.dataUpdatedAt > 0 &&
    Date.now() - summaryQuery.dataUpdatedAt < 60_000;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Summary cards */}
      <Panel
        title="Portfolio Overview"
        description={baselineScope ?? "Positions, P&L, and performance metrics"}
        actions={
          <div className="flex items-center gap-2 text-xs text-muted">
            <span
              className={`h-2 w-2 rounded-full ${isLive ? "bg-green-500 animate-pulse" : "bg-muted/50"}`}
            />
            {isLive ? "Live" : "Stale"}
          </div>
        }
      >
        {summaryQuery.isError ? (
          <QueryErrorState
            what="portfolio summary"
            onRetry={() => void summaryQuery.refetch()}
          />
        ) : summaryQuery.isLoading ? (
          <LoadingState text="Loading portfolio summary..." />
        ) : summary?.note ? (
          // The backend returned its degraded "empty" payload (no PostgreSQL)
          // with an explanatory note. Those zeros are NOT real money figures —
          // surface the note instead of rendering six fake $0.00 cards.
          <DegradedNote title="Portfolio data unavailable" note={summary.note} />
        ) : (
          <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]">
            <StatCard
              label="Total Value"
              value={asCurrency(summary?.total_value ?? 0)}
              tone="default"
              hint={
                summary?.live_open_positions
                  ? `${summary.live_open_positions} live ledger position${summary.live_open_positions === 1 ? "" : "s"}`
                  : baselineScope ?? "Invested + P&L"
              }
            />
            <StatCard
              label="Unrealized P&L"
              value={formatPnl(summary?.unrealized_pnl ?? 0)}
              tone={pnlTone(summary?.unrealized_pnl ?? 0)}
              hint={baselineScope ?? "Open positions"}
            />
            <StatCard
              label="Realized P&L"
              value={formatPnl(summary?.realized_pnl ?? 0)}
              tone={pnlTone(summary?.realized_pnl ?? 0)}
              hint={baselineScope ?? "Closed positions"}
            />
            <StatCard
              label="Win Rate"
              value={`${(summary?.win_rate ?? 0).toFixed(1)}%`}
              tone={(summary?.win_rate ?? 0) >= 55 ? "success" : (summary?.win_rate ?? 0) >= 45 ? "warning" : "danger"}
              hint={`${summary?.closed_positions ?? 0} closed trades`}
            />
            <StatCard
              label="Sharpe Ratio"
              value={(summary?.sharpe_ratio ?? 0).toFixed(2)}
              tone={(summary?.sharpe_ratio ?? 0) >= 1 ? "success" : (summary?.sharpe_ratio ?? 0) >= 0 ? "warning" : "danger"}
              hint="Annualized"
            />
            <StatCard
              label="Max Drawdown"
              value={asCurrency(summary?.max_drawdown ?? 0)}
              tone={(summary?.max_drawdown ?? 0) > 100 ? "danger" : "default"}
              hint="Worst peak-to-trough"
            />
          </div>
        )}
      </Panel>

      <Panel
        title="Live Order Ledger"
        description={`${liveOrders.length} ${baselineScope ? "go-live order" : "recent live order"}${liveOrders.length !== 1 ? "s" : ""}`}
      >
        {liveLedgerQuery.isError ? (
          <QueryErrorState
            what="live order ledger"
            onRetry={() => void liveLedgerQuery.refetch()}
          />
        ) : liveLedgerQuery.isLoading ? (
          <LoadingState text="Loading live orders..." />
        ) : liveOrders.length === 0 ? (
          liveLedgerQuery.data?.note ? (
            // 200-with-note degraded payload (no PostgreSQL): NOT "no orders".
            <DegradedNote title="Live ledger unavailable" note={liveLedgerQuery.data.note} />
          ) : (
            <div className="py-6 text-center">
              <Briefcase className="mx-auto h-8 w-8 text-white/20" />
              <p className="mt-2 text-sm text-muted">No live orders in the ledger.</p>
            </div>
          )
        ) : (
          <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
            <div className="min-w-[40rem] space-y-1">
              <div className="grid grid-cols-[1fr_4rem_5rem_5rem_6rem_6rem] items-center gap-3 px-3 py-1.5">
                <span className="text-xs font-medium text-muted">Order</span>
                <span className="text-center text-xs font-medium text-muted">Side</span>
                <span className="text-right text-xs font-medium text-muted">Notional</span>
                <span className="text-right text-xs font-medium text-muted">Size</span>
                <span className="text-right text-xs font-medium text-muted">Filled</span>
                <span className="text-right text-xs font-medium text-muted">State</span>
              </div>
              {liveOrders.map((order) => (
                <LiveOrderRow key={order.order_key} order={order} />
              ))}
            </div>
          </div>
        )}
      </Panel>

      <Panel
        title="Live Fill Ledger"
        description={`${liveFills.length} ${baselineScope ? "go-live fill" : "recent live fill"}${liveFills.length !== 1 ? "s" : ""}`}
      >
        {liveLedgerQuery.isError ? (
          <QueryErrorState
            what="live fill ledger"
            onRetry={() => void liveLedgerQuery.refetch()}
          />
        ) : liveLedgerQuery.isLoading ? (
          <LoadingState text="Loading live fills..." />
        ) : liveFills.length === 0 ? (
          liveLedgerQuery.data?.note ? (
            <DegradedNote title="Live fills unavailable" note={liveLedgerQuery.data.note} />
          ) : (
            <div className="py-6 text-center">
              <Briefcase className="mx-auto h-8 w-8 text-white/20" />
              <p className="mt-2 text-sm text-muted">No live fills in the ledger.</p>
            </div>
          )
        ) : (
          <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
            <div className="min-w-[40rem] space-y-1">
              <div className="grid grid-cols-[1fr_4rem_5rem_5rem_5rem_6rem] items-center gap-3 px-3 py-1.5">
                <span className="text-xs font-medium text-muted">Fill</span>
                <span className="text-center text-xs font-medium text-muted">Side</span>
                <span className="text-right text-xs font-medium text-muted">Size</span>
                <span className="text-right text-xs font-medium text-muted">Price</span>
                <span className="text-right text-xs font-medium text-muted">Fee</span>
                <span className="text-right text-xs font-medium text-muted">Source</span>
              </div>
              {liveFills.map((fill) => (
                <LiveFillRow key={String(fill.fill_id)} fill={fill} />
              ))}
            </div>
          </div>
        )}
      </Panel>

      {/* Daily P&L chart */}
      <Panel title="Daily P&L" description={baselineScope ?? "Cumulative profit/loss over time"}>
        {historyQuery.isError ? (
          <QueryErrorState
            what="P&L history"
            onRetry={() => void historyQuery.refetch()}
          />
        ) : historyQuery.isLoading ? (
          <LoadingState text="Loading P&L history..." />
        ) : history.length === 0 && historyQuery.data?.note ? (
          <DegradedNote title="P&L history unavailable" note={historyQuery.data.note} />
        ) : (
          <PnlChart data={history} />
        )}
      </Panel>

      {/* Positions table */}
      <Panel
        title="Open Positions"
        description={`${positions.length} ${baselineScope ? "go-live position" : "position"}${positions.length !== 1 ? "s" : ""}`}
      >
        {positionsQuery.isError ? (
          <QueryErrorState
            what="open positions"
            onRetry={() => void positionsQuery.refetch()}
          />
        ) : positionsQuery.isLoading ? (
          <LoadingState text="Loading positions..." />
        ) : sorted.length === 0 ? (
          positionsQuery.data?.note ? (
            <DegradedNote title="Positions unavailable" note={positionsQuery.data.note} />
          ) : (
            <div className="py-8 text-center">
              <Briefcase className="mx-auto h-8 w-8 text-white/20" />
              <p className="mt-2 text-sm text-muted">No open positions.</p>
              <p className="text-xs text-white/25">
                Trade via the Alpha Signals page or Command Center.
              </p>
            </div>
          )
        ) : (
          <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
            <div className="min-w-[36rem] space-y-1">
              {/* Header */}
              <div className="grid grid-cols-[1fr_4rem_5rem_5rem_5rem_5rem_5rem] items-center gap-3 px-3 py-1.5">
                <span className="text-xs font-medium text-muted">Market</span>
                <span className="text-center text-xs font-medium text-muted">Dir</span>
                <div className="text-right">
                  <SortHeader label="Size" field="size" active={sortField === "size"} dir={sortDir} onSort={handleSort} />
                </div>
                <div className="text-right">
                  <SortHeader label="Entry" field="entry_price" active={sortField === "entry_price"} dir={sortDir} onSort={handleSort} />
                </div>
                <div className="text-right">
                  <SortHeader label="Current" field="current_price" active={sortField === "current_price"} dir={sortDir} onSort={handleSort} />
                </div>
                <div className="text-right">
                  <SortHeader label="P&L" field="pnl" active={sortField === "pnl"} dir={sortDir} onSort={handleSort} />
                </div>
                <div className="text-right">
                  <SortHeader label="P&L %" field="pnl_pct" active={sortField === "pnl_pct"} dir={sortDir} onSort={handleSort} />
                </div>
              </div>

              {/* Rows */}
              {sorted.map((pos) => (
                <PositionRow key={pos.id} position={pos} />
              ))}

              {/* Totals row (unrealized only — matches the open-position rows above) */}
              {sorted.length > 0 && (
                <div className="grid grid-cols-[1fr_4rem_5rem_5rem_5rem_5rem_5rem] items-center gap-3 rounded border border-white/10 bg-white/5 px-3 py-2 mt-2">
                  <span className="text-xs font-semibold text-white/60">
                    Total ({sorted.length})
                  </span>
                  <span />
                  <div className="text-right">
                    <span className="text-xs font-mono font-semibold text-white/70">
                      {asCurrency(sorted.reduce((s, p) => s + p.size, 0))}
                    </span>
                  </div>
                  <span />
                  <span />
                  <div className="text-right">
                    <span
                      className={`text-xs font-mono font-semibold ${pnlColor(
                        sorted.reduce((s, p) => s + p.unrealized_pnl, 0),
                      )}`}
                    >
                      {formatPnl(sorted.reduce((s, p) => s + p.unrealized_pnl, 0))}
                    </span>
                  </div>
                  <span />
                </div>
              )}
            </div>
          </div>
        )}
      </Panel>

      {/* Paper trading (simulated account) — clearly separated from the live
          panels above. This mounts the ONLY manual paper close-position UI
          (POST /api/paper/close), previously built but unreachable. */}
      <Panel
        title="Paper Trading"
        description="Simulated paper account — not real money"
      >
        <PortfolioCard />
      </Panel>
      <PositionsTable />
    </div>
  );
}
